<template>
  <div
    class="w-full h-full model-view"
    v-loading="loadingEngine"
    :class="modelWidth === 1 / 2 ? 'w-1/2' : 'w-full'"
  >
    <img
      id="playerEngineId"
      class="engineImg w-full"
      v-if="modelSetting.cloudRenderLoadMode === 'img'"
    />
    <video id="playerEngineId" autoplay playsinline muted class="engineVideo w-full" v-else></video>
    <!-- 模型进度条 -->
    <player-progress
      :loadType="loadType"
      :percentage="loadPercentage"
      v-if="showLoadProgress"
    ></player-progress>
  </div>
</template>

<script>
import { get, max, filter, last } from 'lodash'
import dayjs from 'dayjs'
import {
  generateGUID,
  getPreferenceSettings,
  downloadFile,
} from '../../assets/js/preview/publicMethods'
import preloadService from '../../service/PreloadService'

const PlayerProgress = () => {
  return import('./PlayerProgress')
}
export default {
  name: 'ModelPreview',
  components: { PlayerProgress },
  props: {
    GXRunPlayer: {
      type: Boolean,
      defalut: false,
    },
    cloudRender: {
      type: Object,
      default: () => {},
    },
    loadModelNode: {
      type: Boolean,
      defalut: false,
    },
    modelWidth: {
      type: Number,
      defalut: 1, // 模型的宽度
    },
    modelDetail: {
      type: Object,
      defalut: () => {
        return {
          filePath: '',
          materialFileId: '',
          modelFileId: '', // 模型文件id，云渲染路径又是模型加载名称
        }
      },
    },
  },
  data() {
    return {
      playerInitStatus: false, // player初始化状态
      firstLoad: false, // 首次加载模型触发状态
      player: null, // player实例
      loadingEngine: false, // 引擎加载状态
      showLoadProgress: false, // 进度条显隐
      initHandle: false, // 是否进行了初始化
      loadType: '', // 进度条类型
      sceneRootNode: 0, // 根节点
      loadPercentage: 0, // 进度条值
      modelEngineId: '',
      preloadEngine: null,
      selectedMarkerExist: false,
    }
  },
  created() {
    this.loadingEngine = true
  },
  computed: {
    // 模型相关设置
    modelSetting() {
      return Wiz.globalSettings.globalConfig?.modelSetting || {}
    },
    // 云渲染协议
    cloudProtocol() {
      return this.modelSetting.cloudProtocol || 'ws'
    },
    // 云渲染服务路径
    cloudServerPath() {
      return this.modelSetting.cloudServerPathVulkan || '192.168.20.79:7082'
    },
  },
  watch: {
    'modelDetail.modelFileId': {
      async handler(newVal, oldVal) {
        if (!oldVal && !this.firstLoad && this.playerInitStatus) {
          this.firstLoad = true
          this.loadModelSdk()
        } else if (oldVal) {
          await this.destroyNode(oldVal)
          this.loadModelSdk()
        }
      },
      immediate: true,
    },
    GXRunPlayer(val) {
      if (val && !this.initHandle) {
        this.initHandle = true
        this.init()
      }
    },
  },
  async mounted() {
    if (this.GXRunPlayer && !this.initHandle) {
      this.initHandle = true
      this.init()
    }
    window.addEventListener('beforeunload', async (e) => {
      if (this.modelSetting.preloadServer) {
        this.forceTerminate(this.preloadEngine || this.modelEngineId)
      } else if (this.player) {
        this.cloudRender.Engine.destroyEngine()
      }
    })
  },
  beforeDestroy() {
    if (this.player) {
      this.player.Native.OperatingMode.EventPickUpNode.disconnect(this.pickupWatch)
      this.player.Native.Node.EventSelectNode.disconnect(this.selectWatch)
    }
    if (this.modelSetting.preloadServer) {
      this.forceTerminate(this.preloadEngine || this.modelEngineId)
    } else if (this.player) {
      this.cloudRender.Engine.destroyEngine()
    }
  },
  methods: {
    // 引擎初始化
    init() {
      // 添加预加载配置则优先考虑预加载
      if (this.modelSetting.preloadServer && this.modelDetail.modelFileId) {
        this.getDistributeEngine()
      } else {
        this.runPlayer()
      }
    },
    // 获取预加载分配节点
    async getDistributeEngine() {
      try {
        const [err, res] = await preloadService.getEngineId({
          MD5: this.modelDetail.modelFileId,
          filePath: this.modelDetail.filePath,
        })
        if (!err && res && res.code === 200 && res.data && res.data.engineId) {
          this.runPlayer(res.data.engineId)
        } else {
          this.runPlayer()
        }
      } catch (error) {
        this.runPlayer()
      }
    },
    async destroyNode(fileId) {
      const res = await this.cloudRender.Node.destroyNode(fileId)
    },
    // 强制终止连接
    async forceTerminate(id) {
      if (id) {
        const [err, res] = await preloadService.forceTerminate({
          id,
          t: new Date().valueOf(),
        })
      }
    },
    // 监听模型加载进度
    checkModelLoad(val, node, name) {
      this.loadPercentage = _.round(val * 100, 2)
    },
    // 加载player引擎 sdk
    async runPlayer(engineId) {
      this.preloadEngine = engineId || null
      this.modelEngineId = generateGUID()
      const param = {
        signalServer: `${this.cloudProtocol}://${this.cloudServerPath}`,
        options: {
          videoDom: document.getElementById('playerEngineId'),
          userTimeout: 1000 * 60 * 60, // 用户操作超时设置，毫秒
          engineId: engineId || this.modelEngineId, // 引擎会话id，如果已存在则连接，没有则创建，会话在断开连接后超时自动关闭
          exitTimeout: 1000 * 60, // 用户超时后多久断开会话
          maxTimeout: 1000, // 会话超时时间，允许掉线后引擎保存多久，在该时间内可重新连接到会话，否则会创建新的会话
          // openDebug: true,
        },
      }
      const { data } = await this.cloudRender.Engine.loadEngine(param)
      this.loadingEngine = false
      console.log('创建的GX引擎', data)
      this.player = data.getDataInfo()
      this.cloudRender.Dispatch.on('Engine', async (e) => {
        if (e.type === 'UserTimeOut') {
          // 用户操作超时
          console.error('用户操作超时')
        }
        if (e.type === 'TimeOutExit') {
          // 断开会话
          console.error('超时断开')
        }
      })
      window.player = this.player
      window.cloudRender = this.cloudRender
      this.loadingEngine = false
      // 实例化完成将实例化后的player传出去
      this.$emit('sendPlayer', this.player)
      this.playerInitStatus = true
      this.initPlayer(engineId)
    },
    // 初始化player
    async initPlayer(engineId) {
      // 自动更新画面
      const autoRes = await this.cloudRender.Setting.set({
        name: 'sys.autoUpdateFrame',
        type: 'bool',
        value: false,
      })
      const settingsFileUrl = `data://../apps/${this.modelSetting.folderName}/settings.json`
      const settingsData = await this.player.Native.File.loadText(settingsFileUrl)
      if (settingsData) {
        const defaultSettings = JSON.parse(settingsData)
        try {
          const res = await this.cloudRender.Setting.set(defaultSettings.Show)
        } catch (error) {
          console.error(error)
        }
      }
      if (this.modelDetail.modelFileId && !this.firstLoad && !engineId) {
        this.firstLoad = true
        this.loadModelSdk()
      }
      // 获取并加载偏好设置
      // await getPreferenceSettings(true, this.player)
      this.cloudRender.Setting.set({
        name: 'func.nodetips.enable',
        value: false,
      })
      // 点选监听
      this.player.Native.OperatingMode.EventPickUpNode.connect(this.pickupWatch)
      this.player.Native.Node.EventSelectNode.connect(this.selectWatch)
    },
    // 加载模型
    async loadModelSdk() {
      this.cloudRender.Dispatch.on('loadNode', this.loadModelConnect)
      const query = {
        targetUrl: this.modelDetail.filePath,
        fileUrl: `${
          Wiz.GetApiClientInstance('file-management')?.basePath
        }/api/file-management/files/${this.modelDetail.modelFileId}/download`,
        tempPath: `data://../temps/models/${this.modelDetail.modelFileId}/${
          this.modelDetail.modelFileId
        }.${_.last(this.modelDetail.filePath.split('.')) || 'pr'}`,
        options: {
          nodeId: this.modelDetail.modelFileId,
          httpOptions: {
            methods: 'GET',
            header: {
              Authorization: `Bearer ${Wiz.AuthHooks.GetToken()}`,
            },
          },
        },
      }
      if (this.modelDetail.materialFileId) {
        query.options.modelDecals = {
          targetUrl: `data://../apps/${this.modelSetting.folderName}/models/${this.modelDetail.modelFileId}/${this.modelDetail.modelFileId}.zip`,
          fileUrl: `${
            Wiz.GetApiClientInstance('file-management')?.basePath
          }/api/file-management/files/${this.modelDetail.materialFileId}/download`,
        }
      }
      const res = await this.cloudRender.Node.addNode(query)
    },
    // 加载、下载进度
    loadModelConnect: _.throttle(async function loadMsg(res) {
      if (res.status && res.data && res.data.type === 'nodeDecals') {
        switch (res.data.currentState) {
          case 1:
            this.showLoadProgress = true
            this.loadType = '贴图加载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 3:
            this.showLoadProgress = true
            this.loadType = '贴图下载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 4:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            break
          case 5:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            break
          default:
            break
        }
      } else if (res.status && res.data && res.data.type === 'nodeStatus') {
        switch (res.data.currentState) {
          case 1:
            this.showLoadProgress = true
            this.loadType = '模型加载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 3:
            this.showLoadProgress = true
            this.loadType = '模型下载中...'
            this.loadPercentage = _.round(res.data.process, 2)
            break
          case 4:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            this.cloudRender.Dispatch.off('loadNode', this.loadModelConnect)
            const nodeRes = this.cloudRender.Node.getInfo(this.modelDetail.modelFileId)
            const moveRes = await this.cloudRender.Camera.moveCamera(
              'Node',
              {
                id: nodeRes.data.id,
              },
              2
            )
            setTimeout(() => {
              this.$emit('loadingSwitch', { bol: false, title: '模型加载中' })
              this.$emit('loadModelEnd', nodeRes.data.dataInfo?.node || null)
            }, 2000)
            break
          case 5:
            this.showLoadProgress = false
            this.loadType = ''
            this.loadPercentage = 0
            break
          default:
            break
        }
      } else if (!res.status) {
        this.$message.warning(res.message)
        this.showLoadProgress = false
        this.loadType = ''
        this.loadPercentage = 0
      }
    }, 500),
    // 更新选中模型图标
    async updateSelectMark(code, node) {
      const text = `Code: ${code}`
      if (!code) {
        if (this.selectedMarkerExist) {
          await this.cloudRender.Marker.updateBySingleLine({
            id: 'selectCodeMarker',
            visible: false,
          })
        }
        return
      }
      const res = await this.player.Native.Node.getProperty(node, ['aabb'])
      const pos = []
      if (res && !_.isEmpty(res)) {
        pos[0] = res.aabb[0][0] + (res.aabb[1][0] - res.aabb[0][0]) * 0.5 // x坐标
        pos[1] = res.aabb[0][1] + (res.aabb[1][1] - res.aabb[0][1]) * 0.5 // y坐标
        pos[2] = res.aabb[0][2] + (res.aabb[1][2] - res.aabb[0][2]) // z坐标
      }
      if (!_.isNil(pos)) {
        const newOption = {
          id: 'selectCodeMarker',
          pos,
          background: '#00000099',
          visible: true,
          text,
          textColor: '#ffffffff',
          nodeOffset: [0, -60],
          markPosType: 1,
        }
        if (this.selectedMarkerExist) {
          await this.cloudRender.Marker.updateBySingleLine(newOption)
        } else {
          const result = await this.cloudRender.Marker.createBySingleLine(newOption)
          if (result.status) {
            this.selectedMarkerExist = true
          }
        }
      }
    },
    // // 接口选中监听
    async selectWatch(node, status) {
      let res = {}
      if (status && node) {
        res = await this.player.Native.Node.getProperty(node, ['code', 'name'])
      } else {
        await this.cloudRender.Marker.updateBySingleLine({
          id: 'selectCodeMarker',
          visible: false,
        })
        return
      }
      this.upDateCodeMsg(res)
    },
    // 点选选中监听
    async pickupWatch(data) {
      let res = {}
      if (data.node) {
        res = await this.player.Native.Node.getProperty(data.node, ['code', 'name'])
      } else {
        await this.cloudRender.Marker.updateBySingleLine({
          id: 'selectCodeMarker',
          visible: false,
        })
        return
      }
      this.upDateCodeMsg(res)
    },
    // 选中更新
    async upDateCodeMsg(res) {
      this.updateSelectMark(res?.code ? res.code : '', res.node)
      this.$emit('updateModelNode', res || {})
      localStorage.setItem('currentModelNodeCode', res?.code ? res.code : '')
    },
  },
}
</script>

<style lang="scss" scoped>
.engineVideo {
  height: calc(100% + 15px);
  object-fit: contain;
}

.engineImg {
  height: calc(100% + 15px);
}

::v-deep .el-loading-mask {
  background-color: #fff !important;
  opacity: 1 !important;
}

.model-view {
  transform: scale(1, 1);
  transform-origin: 'top left';
}
</style>
